/**
 * SF-4656 — the one record of which company has a save running on the company
 * detail page, for the life of the app (not of a page or a tab).
 *
 * Every write the page makes about a company goes through here: the
 * single-field company saves (useSaveCompanyField: Feature Flags switches,
 * General Info controls, the CSAT company-default radio), the external AI
 * switch (McpAccessSwitch), and the SSO setup's save/delete (SsoConfigTab).
 * The rule is ONE save at a time per company:
 *  - `beginCompanySave` admits a save synchronously, or refuses it (returns
 *    null) while another one runs for that company. So a confirmation left
 *    open, a second click, or a control that remounted cannot start a second
 *    overlapping write; overlapping writes each put a whole company into the
 *    cache, so the later reply could hide the other change.
 *  - it lives outside React, so leaving a company (or the page) and coming
 *    back while a save runs still shows it running and keeps everything
 *    locked until it ends.
 *  - each save ends only its own entry (token), in a `finally`.
 */
import { useSyncExternalStore } from 'react';
import type { Company } from '../list';

export interface RunningCompanySave {
  /** `field`: one company field; `sso`: the SSO setup record (auth service). */
  kind: 'field' | 'sso';
  field?: keyof Company;
  /** For `field` saves: the value it is heading to (shown while locked). */
  value?: unknown;
  token: number;
}

const running = new Map<string, RunningCompanySave>();
const listeners = new Set<() => void>();
let nextToken = 0;

const notify = () => listeners.forEach((l) => l());

/** Starts a save for the company, or returns null while another one runs. */
export function beginCompanySave(
  companyId: string,
  save: Omit<RunningCompanySave, 'token'>,
): number | null {
  if (!companyId || running.has(companyId)) return null;
  const token = ++nextToken;
  running.set(companyId, { ...save, token });
  notify();
  return token;
}

/** Updates the value a running save is heading to (e.g. what the server confirmed). */
export function updateCompanySave(companyId: string, token: number, value: unknown) {
  const entry = running.get(companyId);
  if (entry?.token !== token) return;
  running.set(companyId, { ...entry, value });
  notify();
}

/** Ends a save; only the save that started it can end it. */
export function endCompanySave(companyId: string, token: number) {
  if (running.get(companyId)?.token !== token) return;
  running.delete(companyId);
  notify();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The save running for this company right now, or null. Re-renders on change. */
export function useRunningCompanySave(companyId: string | undefined): RunningCompanySave | null {
  return useSyncExternalStore(subscribe, () => (companyId ? running.get(companyId) ?? null : null));
}

/** Tests only: forget every running save between test cases. */
export function resetCompanySaves() {
  running.clear();
  notify();
}
